/**
 * Summary filled/total counts and visible rows — same rules as step nav + subsection badges.
 */

import {
  filterSummaryFieldMappingsForFormView,
  type FormFieldVisibilityContext,
} from '../utils/formFieldVisibility';
import { isSummaryFieldValueFilled } from '../utils/subsectionHeaderMetrics';

import type { SummaryCardRow } from './summaryCardSourceTypes';

function isSummaryValueFilled(value: unknown): boolean {
  return isSummaryFieldValueFilled(value);
}

/** Rows the user can see in the current form view (basic/advanced + RBAC). */
export function filterSummaryRowsForFormView(
  rows: readonly SummaryCardRow[],
  visibility?: FormFieldVisibilityContext,
): SummaryCardRow[] {
  if (!visibility) {
    return [...rows];
  }

  const visibleMappings = filterSummaryFieldMappingsForFormView(
    rows.map(row => ({
      dataField: row.dataField,
      permissionSection: row.permissionSection,
      permissionField: row.permissionField,
    })),
    visibility,
  );
  const visibleFields = new Set(
    visibleMappings.map(mapping => mapping.dataField),
  );
  return rows.filter(row => visibleFields.has(row.dataField));
}

export function countSummaryRowMetrics(
  rows: readonly SummaryCardRow[],
  data: Record<string, unknown>,
): { filled: number; total: number } {
  const total = rows.length;
  const filled = rows.filter(row =>
    isSummaryValueFilled(data[row.dataField]),
  ).length;
  return { filled, total };
}

/** Single contract: visible rows + counts from the same filtered list. */
export function resolveSummarySectionMetrics(
  rows: readonly SummaryCardRow[],
  data: Record<string, unknown>,
  visibility?: FormFieldVisibilityContext,
): {
  visibleRows: SummaryCardRow[];
  filled: number;
  total: number;
} {
  const visibleRows = filterSummaryRowsForFormView(rows, visibility);
  const { filled, total } = countSummaryRowMetrics(visibleRows, data);
  return { visibleRows, filled, total };
}
